import React, { createContext, useContext, useEffect, useState } from "react";
import axios from 'axios';
import { useInventoryProductContext } from "../InventoryProductContext";

const CartAccountContext = createContext();

export const CartAccountProvider = ({ children }) => {
    const {products} = useInventoryProductContext()
    const validNewCustomer = localStorage.getItem('validNewCustomer') === 'true';
    const validWithOTP = localStorage.getItem('validWithOTP') === 'true';
    const validSalesPerson = localStorage.getItem('validSalesPerson') === 'true';
    const accountCacheName = "cart-account-cache";
    const [accountDetails, setAccountDetails] = useState([]);
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 2000;

    const fetchWithRetry = async (fetchFunction, maxRetries = MAX_RETRIES) => {
        let retries = 0;
        while (retries < maxRetries) {
            try {
                await fetchFunction();
                return; // Exit loop if fetchFunction is successful
            } catch (error) {
                console.error(`Attempt ${retries + 1} failed: ${error.message}`);
                retries++;
                if (retries < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
                }
            }
        }
        console.error('Max retries reached. Unable to fetch data.');
    };

    const fetchAccountDetails = async () => {
        try {
            const data = JSON.parse(sessionStorage.getItem('userData'));
            const tempContactGuid = data?.contactId;
            if (!tempContactGuid) {
                // console.log('Contact GUID is not present.');
                return;
            }

            const cache = await caches.open(accountCacheName);
            const cachedResponse = await cache.match('/api/cart/fetch-account-owner-details');

            if (cachedResponse) {
                const cachedData = await cachedResponse.json();
                setAccountDetails(cachedData.accounts || cachedData);
            } else {
                const response = await axios.post(
                    `${process.env.REACT_APP_SERVER_URL}/api/cart/fetch-account-owner-details`,
                    { contactGuid: tempContactGuid }
                );

                const formattedData = response.data.accounts || response.data;

                const responseClone = new Response(JSON.stringify(formattedData), {
                    headers: { 'Content-Type': 'application/json' },
                });
                await cache.put('/api/cart/fetch-account-owner-details', responseClone);

                setAccountDetails(formattedData);
            }
        } catch (err) {
            // console.log(err.message);
        }
    };

    const fetchSalesAccountDetails = async () => {
        try {
            const cache = await caches.open(accountCacheName);
            const cachedResponse = await cache.match('/api/cart/all-active-account-owner');

            if (cachedResponse) {
                const cachedData = await cachedResponse.json();
                setAccountDetails(cachedData.accounts || cachedData);
            } else {
                const response = await axios.get(
                    `${process.env.REACT_APP_SERVER_URL}/api/cart/all-active-account-owner`
                );

                const formattedData = response.data.accounts || response.data;

                const responseClone = new Response(JSON.stringify(formattedData), {
                    headers: { 'Content-Type': 'application/json' },
                });
                await cache.put('/api/cart/all-active-account-owner', responseClone);

                setAccountDetails(formattedData);
            }
        } catch (err) {
            // console.log(err.message);
        }
    };

    useEffect(() => {
        const fetchCartAccountDetails = () => {
            if (validSalesPerson) {
                fetchWithRetry(fetchSalesAccountDetails);
            } else {
                const checkUserDataAndFetch = async () => {
                    const waitForUserData = (retries = 5, delay = 500) => {
                        return new Promise((resolve, reject) => {
                            let attempts = 0;
                            const interval = setInterval(() => {
                                const userData = JSON.parse(sessionStorage.getItem('userData'));
                                if (userData) {
                                    clearInterval(interval);
                                    resolve(userData);
                                } else {
                                    attempts++;
                                    if (attempts >= retries) {
                                        clearInterval(interval);
                                        reject(new Error('userData not found in sessionStorage after multiple attempts'));
                                    }
                                }
                            }, delay);
                        });
                    };
    
                    try {
                        const userData = await waitForUserData();
                        fetchWithRetry(fetchAccountDetails);
                    } catch (error) {
                        console.error(error.message);
                    }
                };
    
                checkUserDataAndFetch();
            }
        }
        if(validWithOTP){
            fetchCartAccountDetails()
        }
    }, [products]);

    return (
        <CartAccountContext.Provider value={{ accountDetails, accountCacheName }}>
            {children}
        </CartAccountContext.Provider>
    );
};

export const useCartAccountContext = () => {
    return useContext(CartAccountContext);
};
